import ErrorBoundary from "@/modules/ErrorBoundary";
import routes from "@/routes";
import { lazy } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

const TemplateStudio = lazy(() => import("@/modules/Asset/TemplateStudio"));
const Gallery = lazy(() => import("@/modules/Asset/TemplateStudio/Gallery"));
const Builder = lazy(() => import("@/modules/Asset/TemplateStudio/Builder"));
const Done = lazy(() => import("@/modules/Asset/TemplateStudio/Done"));

/**
 * Keeps old links working.
 *
 * `detail/:id` and `edit/:id` were the two screens the studio replaces. Nothing in the app links to
 * either — the nav drawer points at the index — so these exist for bookmarks and for URLs people
 * have pasted into tickets. The search string is preserved, because an old `edit/:id` link can
 * carry a `threadId` worth keeping.
 */
function RedirectToBuilder(): JSX.Element {
  const { id } = useParams();
  const { search } = useLocation();
  return (
    <Navigate
      replace
      to={`${routes.DASHBOARD}/${routes.ASSETS.TEMPLATE.INDEX}/${routes.ASSETS.TEMPLATE.BUILD(
        id ?? ""
      )}${search}`}
    />
  );
}

const TemplateRoutes = {
  element: <TemplateStudio />,
  path: routes.ASSETS.TEMPLATE.INDEX,

  // Contains a studio crash to the studio. `App.tsx` already sets a root `errorElement`, but the
  // root boundary replaces the whole shell — a thrown error in a preview would blank the dashboard,
  // nav included. Declared here, the boundary renders inside the Templates area and the rest of the
  // app stays usable.
  errorElement: <ErrorBoundary />,

  children: [
    {
      element: <Gallery />,
      path: "",
    },
    {
      element: <Builder />,
      path: routes.ASSETS.TEMPLATE.BUILD(),
    },
    {
      element: <Done />,
      path: routes.ASSETS.TEMPLATE.DONE(),
    },
    {
      element: <RedirectToBuilder />,
      path: routes.ASSETS.TEMPLATE.DETAIL(),
    },
    {
      element: <RedirectToBuilder />,
      path: routes.ASSETS.TEMPLATE.EDIT(),
    },
  ],
};

export default TemplateRoutes;
